<script lang="ts" setup>
import { useResponsive } from "@core/composables"
import { formatIdSource } from "@core/helpers"
import { getSourceConfigByAmount } from "@modules/energy-manager/helpers"
import { useEmConsoCompareStore } from "@modules/energy-manager/stores/em-conso-compare.store"
import { useVModel } from "@vueuse/core"
import { computed, ref, watch } from "vue"
import { useI18n } from "vue-i18n"
import { ColumnConfig } from "@types"

import { METER_TYPES_LIST, SOURCES_TYPES } from "@core/constants"

const { isMobile } = useResponsive()
const emConsoCompareStore = useEmConsoCompareStore()

type Props = {
    visible: boolean
}
const emit = defineEmits([
    "update:visible",
    "click:folder",
    "click:building",
    "click:source",
])
const props = defineProps<Props>()

// ============================================
// Gestion de la visibilité de la modale
// ============================================

const visible = useVModel(props, "visible", emit)

// ============================================
// Gestion du choix de comparaison
// ============================================
const modelCompareOption = ref("all")
/**
 * Liste des options de comparaison
 */
const getOptionsList = computed(() => [
    { label: t("options.all"), value: "all" },
    {
        label: t("options.folder"),
        value: "folder",
    },
    { label: t("options.building"), value: "building" },
])

watch(modelCompareOption, (newValue, oldValue) => {
    if (newValue !== oldValue) {
        selectedRows.value = []
    }
})
/**
 * Liste des bâtiments ou dossiers sélectionnés
 */
const selectedRows = ref<any[]>([])
/**
 * Quand l'utilisateur valide la liste des bâtiments à comparer,
 * met à jour le store et ferme la modale
 */
function onSubmit() {
    // met à jour le store avec les éléments sélectionnés
    if (modelCompareOption.value === "folder") {
        emConsoCompareStore.compareListId = selectedRows.value.flatMap(
            ({ data: folder }) =>
                folder.buildings.map(
                    (building: { _id_building: string }) =>
                        building._id_building
                )
        )
        emConsoCompareStore.compareChoice = "folder"
        emConsoCompareStore.compareListIdByFolder = selectedRows.value.map(
            ({ data: folder }) => folder._id_folder
        )
    } else if (modelCompareOption.value === "building") {
        emConsoCompareStore.compareListId = selectedRows.value.map(
            (building: { _id_building: string }) => building._id_building
        )
        emConsoCompareStore.compareChoice = "building"
    } else {
        emConsoCompareStore.compareListId = emConsoCompareStore.buildings.map(
            (building) => building._id_building
        )
        emConsoCompareStore.compareChoice = "all"
    }
    visible.value = false
}
// ============================================
// Gestion de la configuration du tableau
// ============================================

/**
 * Configuration du tableau en fonction du choix de comparaison
 * Renvoie les colonnes et les valeurs à afficher
 */
const getValuesAndColumnsByCompareChoice = computed<{
    value: any[]
    columns: ColumnConfig[]
}>(() => {
    let columns: ColumnConfig[] = []
    let value: any[] = []
    if (modelCompareOption.value === "folder") {
        // On applique la fonction de transformation pour obtenir un tableau de TreeNode
        value = transformFolderTreeToTreeNodes(
            emConsoCompareStore.folderTreeList
        )
        columns = [
            {
                field: "folder",
                header: t("folder", { count: selectedRows.value.length }),
                link: true,
                sortable: true,
                style: { width: "10rem" },
                expander: true,
            },
            {
                field: "building",
                header: "Nb de bâtiments",
                subLink: (data: { building: number }) => data.building > 0,
                sortable: true,
                style: { width: "10rem" },
                expander: false,
            },
            {
                field: "source",
                header: "Nb de sources",
                subLink: (data: { source: number }) => data.source > 0,
                sortable: true,
                style: { width: "8rem" },
                expander: false,
            },
        ]
    } else {
        value = emConsoCompareStore.buildings.map((building: any) => {
            return {
                building: building.name,
                address: building.address?.inline_name,
                folder: building.folder?.name,
                source: building.sources.filter(
                    (source: any) =>
                        !["idopenmeteo", "idmeteofrance"].includes(
                            source.idtype
                        )
                ).length,
                tag: building.tags?.map((tag: any) => tag.name),
                _id_building: building._id_building,
                _id_folder: building.folder?._id_folder,
                sources: building.sources.filter(
                    (source: any) =>
                        !["idopenmeteo", "idmeteofrance"].includes(
                            source.idtype
                        )
                ),
            }
        })
        columns = [
            {
                field: "building",
                header: t("building", { count: selectedRows.value.length }),
                link: true,
                sortable: true,
                style: { width: "10rem" },
            },
            {
                field: "address",
                header: "Adresse",
                link: true,
                sortable: true,
                style: { width: "10rem" },
            },
            {
                field: "folder",
                header: "Emplacement",
                link: true,
                sortable: true,
                style: { width: "10rem" },
            },
            {
                field: "source",
                header: "Nb de sources",
                subLink: true,
                sortable: true,
                style: { width: "8rem" },
            },
            {
                field: "tag",
                header: "Étiquettes",
                chip: true,
                sortable: true,
                style: { width: "11rem" },
            },
        ]
    }

    return {
        value,
        columns,
    }
})

// ============================================
// Gestion des sous tableaux
// ============================================
const selectedRow = ref()

function onClickPopup({ data, field }: { data: any; field: string }) {
    selectedRow.value = {
        ...data,
        type: field,
    }
}
/**
 * Récupère le détail des sous tableaux
 * en fonction de l'élément sélectionné.
 */
const getDataPopupTable = computed<{
    value: any[]
    columns: ColumnConfig[]
}>(() => {
    if (!selectedRow.value) return { value: [], columns: [] }

    // si on visualise un tableau de bâtiment
    if (selectedRow.value.type === "building") {
        return {
            value: selectedRow.value.buildings.map((building: any) => {
                return {
                    building: building.name,
                    address: building.address?.inline_name,
                    _id_building: building._id_building,
                }
            }),
            columns: [
                {
                    field: "building",
                    header: "Nom",
                    link: true,
                    sortable: true,
                    style: { width: "8rem" },
                },
                {
                    field: "address",
                    header: "Adresse",
                    link: true,
                    sortable: true,
                    style: { width: "10rem" },
                },
            ],
        }
    }
    // si on visualise un tableau de source
    return {
        value: selectedRow.value.sources.map(
            (source: {
                idtype: string
                idsource: string
                _id_source: string
                segment: string
            }) => {
                let type = "Capteur"

                if (METER_TYPES_LIST.includes(source.idtype)) {
                    type = source.idtype.toUpperCase()
                }

                if (source.idtype === SOURCES_TYPES.PDL) {
                    // ajoute le segment
                    type =
                        `${type} ${source.segment && source.segment !== "INDET" ? `(${source.segment})` : ""}`?.trim()
                }

                return {
                    type,
                    idtype: source.idtype,
                    source: formatIdSource(source.idsource, source.idtype),
                    _id_source: source._id_source,
                }
            }
        ),
        columns: [
            { field: "type", header: "Type", style: { width: "6rem" } },
            {
                field: "source",
                header: "Identifiant",
                sortable: true,
                link: true,
                style: { width: "10rem" },
            },
        ],
    }
})

/**
 * Détermine le type d'icône à afficher pour les sources
 */
function getIconBySourceType(type: string) {
    switch (type.toLowerCase()) {
        case "pdl":
        case "elecsavoie":
            return {
                icon: "fa-light fa-bolt",
                color: "#EEC137",
            }
        case "pce":
        case "regaz":
            return {
                icon: "fa-light fa-fire",
                color: "#FA8E42",
            }
        default:
            return {
                icon: "fa-light fa-sensor",
                color: "#35C4DC",
            }
    }
}

// ============================================
// Gestion des redirections
// ============================================
function onClickCell({ data, field }: { data: any; field: string }) {
    if (["building", "address"].includes(field)) {
        emConsoCompareStore.clickLink(data._id_building, "building")
    } else if (field === "folder") {
        emConsoCompareStore.clickLink(data._id_folder, "folder")
    } else if (field === "location") {
        emConsoCompareStore.clickLink(data._id_folder_parent, "folder")
    } else if (field === "source") {
        emConsoCompareStore.clickLink(data._id_source, "source")
    }
}

//==============================================
// Transformation en TreeNode
//==============================================
// Définition d'une interface TreeNode (vous pouvez l'adapter selon vos besoins)
interface TreeNode {
    key: string
    folder: string
    data?: {
        building: number
        source: number
        [key: string]: any
    }
    children?: TreeNode[]
}

// Fonction qui transforme un tableau de dossiers en arbre de TreeNode
function transformFolderTreeToTreeNodes(folderTree: any[]): TreeNode[] {
    return folderTree.map((folder) => {
        return {
            key: folder._id,
            folder: folder.name,
            data: {
                _id_folder: folder._id,
                folder: folder.name,
                building: folder.buildings ? folder.buildings.length : 0,
                source: folder.sources ? folder.sources.length : 0,
                buildings: folder.buildings,
                sources: folder.sources.filter(
                    (source: { idtype: string }) =>
                        !["idopenmeteo", "idmeteofrance"].includes(
                            source.idtype
                        )
                ),
            },
            children:
                folder.children && folder.children.length > 0
                    ? transformFolderTreeToTreeNodes(folder.children)
                    : [],
        }
    })
}

// ============================================
// Traduction
// ============================================
const { t } = useI18n({
    messages: {
        fr: {
            title: "Base de l'analyse",
            quit: "Quitter",
            submit: "Valider",
            options: {
                folder: "Un ou plusieurs dossiers",
                all: "Tout le parc",
                building: "Un ou plusieurs bâtiments",
            },
            limit: {
                title: "Attention, volume de sources élevé",
                content:
                    "En raison du volume important de sources ou de bâtiments dans votre sélection (<span class='font-bold'>{building} bâtiments avec {source} sources</span>), certaines combinaisons de variables, périodes et fréquences pourraient être trop volumineuses pour être calculées.",
            },
            building: `Bâtiments (0) | Bâtiments (1) | Bâtiments ({count})`,
            folder: `Dossiers (0) | Dossiers (1) | Dossiers ({count})`,
        },
    },
})

// ============================================
// Gestion du messages sur le nombre max
// ============================================
/**
 * Liste le nombre de bâtiment/source en fonction du choix de comparaison
 */
const getTotalBuildingSource = computed(() => {
    if (modelCompareOption.value === "all") {
        return {
            building: emConsoCompareStore.buildings.length,
            source: emConsoCompareStore.buildings
                .flatMap((building) => building.sources)
                .filter(
                    (source: { idtype: string }) =>
                        !["idopenmeteo", "idmeteofrance"].includes(
                            source.idtype
                        )
                ).length,
        }
    }
    if (modelCompareOption.value === "folder") {
        return {
            building: selectedRows.value.reduce(
                (acc, { data: folder }) => acc + folder?.buildings.length,
                0
            ),
            source: selectedRows.value.reduce(
                (acc, { data: folder }) => acc + folder.sources.length,
                0
            ),
        }
    } else
        return {
            building: selectedRows.value.length,
            source: selectedRows.value.reduce(
                (acc, building) => acc + building.sources.length,
                0
            ),
        }
})

/**
 * Renvoie la clé utilisée dans le tableau selon modelCompareoption
 */
const getDataKey = computed(() => {
    if (modelCompareOption.value === "folder") {
        return "_id_folder"
    }
    if (modelCompareOption.value === "building") {
        return "_id_building"
    }
    // Si c'est "all", choisis une clé par défaut, par ex. "_id_building"
    return "_id_building"
})

/**
 * Indique si l'utilisateur a atteint la limite de source autorisée pour une bonne performance
 */
const hasReachSourceLimit = computed(() => {
    return getSourceConfigByAmount(getTotalBuildingSource.value.source)
        ?.messageLimit
})
</script>

<template>
    <BaseDialog
        v-model:visible="visible"
        :position="isMobile ? 'center' : 'top'"
        modal
        class="dashboard-modal-select-building"
        :style="{ width: '71rem' }"
        :pt="{
            footer: 'dashboard-model-select-building-footer',
            header: 'dashboard-model-select-building-header',
        }"
        :breakpoints="{ '1199px': '90vw' }"
    >
        <template #header>
            <div class="dashboard-modal-select-building__header">
                <h2
                    class="text-xl font-bold dashboard-mrodal-select-building__title"
                >
                    {{ t("title") }}
                </h2>
                <div class="dashboard-modal-select-building__options">
                    <div
                        class="dashboard-modal-select-building__option"
                        v-for="option in getOptionsList"
                        :key="option.value"
                    >
                        <BaseRadioButton
                            v-model="modelCompareOption"
                            :input-id="option.value"
                            :name="option.value"
                            :value="option.value"
                        />
                        <label
                            :for="option.value"
                            class="dashboard-modal-select-building__option-label"
                            >{{ option.label }}
                        </label>
                    </div>
                </div>
            </div>
        </template>
        <BaseAppMessage severity="warn" v-if="hasReachSourceLimit">
            <template #header>
                {{ t("limit.title") }}
            </template>
            <p
                v-html="
                    t('limit.content', {
                        building: getTotalBuildingSource.building,
                        source: getTotalBuildingSource.source,
                    })
                "
            ></p>
        </BaseAppMessage>
        <div
            class="dashboard-modal-select-building__table"
            v-if="modelCompareOption !== 'all'"
        >
            <BaseTableTree
                v-if="modelCompareOption === 'folder'"
                v-model:selection="selectedRows"
                :value="getValuesAndColumnsByCompareChoice.value"
                :columns="getValuesAndColumnsByCompareChoice.columns"
                data-key="key"
                selection-mode="checkbox"
                paginator
                :rows-per-page-options="[5, 10, 15]"
                :rows="5"
                scrollable
                selectable
                search
                :size="'small'"
                @click:popup="onClickPopup"
                @click:cell="onClickCell"
                ><template #popup>
                    <BaseTable
                        class="dashboard-modal-select-building__table-popup"
                        v-if="selectedRow"
                        data-key="_id_source"
                        :value="getDataPopupTable.value"
                        :columns="getDataPopupTable.columns"
                        scrollable
                        :paginator="getDataPopupTable.value.length > 5"
                        small-paginator
                        :rows="5"
                        @click:cell="onClickCell"
                    >
                        <template #cell_type="{ data: { idtype, type } }">
                            <BaseIcon
                                v-if="getIconBySourceType(idtype).icon"
                                :icon="getIconBySourceType(idtype).icon"
                                :color="getIconBySourceType(idtype).color"
                                size="sm"
                                class="mr-2"
                            />
                            <p>
                                {{ type }}
                            </p>
                        </template>
                    </BaseTable>
                </template>
            </BaseTableTree>
            <BaseTable
                v-else
                v-model:selection="selectedRows"
                :columns="getValuesAndColumnsByCompareChoice.columns"
                :value="getValuesAndColumnsByCompareChoice.value"
                :data-key="getDataKey"
                selectable
                paginator
                :rows-per-page-options="[5, 10, 15]"
                :rows="5"
                scrollable
                search
                :size="'small'"
                @click:popup="onClickPopup"
                @click:cell="onClickCell"
            >
                <template #popup>
                    <BaseTable
                        class="dashboard-modal-select-building__table-popup"
                        v-if="selectedRow"
                        data-key="_id_source"
                        :value="getDataPopupTable.value"
                        :columns="getDataPopupTable.columns"
                        scrollable
                        :paginator="getDataPopupTable.value.length > 5"
                        small-paginator
                        :rows="5"
                        @click:cell="onClickCell"
                    >
                        <template #cell_type="{ data: { idtype, type } }">
                            <BaseIcon
                                v-if="getIconBySourceType(idtype).icon"
                                :icon="getIconBySourceType(idtype).icon"
                                :color="getIconBySourceType(idtype).color"
                                size="sm"
                                class="mr-2"
                            />
                            <p>
                                {{ type }}
                            </p>
                        </template>
                    </BaseTable>
                </template>
                <template #cell_tag="{ data: { tag, _id_building } }">
                    <ListBuildingTags
                        :tags="tag"
                        :building-id="_id_building"
                        readonly
                    ></ListBuildingTags>
                </template>
            </BaseTable>
        </div>

        <template #footer>
            <BaseButton
                :label="t('quit')"
                text
                @click="visible = false"
            ></BaseButton>
            <BaseButton
                :label="t('submit')"
                @click="onSubmit"
                :disabled="selectedRows.length === 0"
            ></BaseButton>
        </template>
    </BaseDialog>
</template>

<style scoped>
@import "@styles/breakpoints.css";

.dashboard-modal-select-building__header {
    display: flex;
    flex-direction: column;
    gap: 2rem;
    align-items: center;
}
.dashboard-modal-select-building {
    .dashboard-modal-select-building__title {
        text-align: center;
        width: 100%;
    }
    .dashboard-modal-select-building__options {
        display: flex;
        gap: 0.75rem;
        justify-content: center;

        @media (--medium-viewport) {
            flex-direction: column;
        }
    }
    .dashboard-modal-select-building__option {
        display: flex;
        align-items: center;
        gap: 0.56rem;
        border-radius: 0.375rem;
        padding: 0.75rem;
        background: #f6f9fc;
        overflow: hidden;
    }
}
</style>

<style>
.dashboard-model-select-building-footer {
    justify-content: space-between !important;
}
.dashboard-model-select-building-header {
    .p-dialog-header-actions {
        justify-content: flex-end;
        height: 1px !important;
    }
    align-items: normal !important;
    justify-content: space-between;
    flex-direction: column-reverse;
}
.dashboard-modal-select-building__table-popup {
    max-width: 30rem;
}
.dashboard-modal-select-building__option-label {
    display: flex;
    align-items: center;
    gap: 0.5rem;
}
</style>
